/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { useRadiusData } from "./hooks";
import { validateRadius } from "./helpers";
import { isError } from "../../../../utils/validations";
import { useSelector } from "react-redux";
import { errorMsgSelector } from "../../../../store/helpers/helperSelectors";
import { setErrorMsg } from "../../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../../store/hooks";
import { InputField } from "../../../Shared/Radix/Inputs/InputField";
import { PasswordField } from "../../../Shared/Radix/Inputs/PasswordField";
import { Label } from "../../../ui/label";
import { Checkbox } from "../../../ui/checkbox";
import { Button } from "../../../ui/button";
import { ShadcnHeading } from "../../../ui/shadcn-heading";

const Radius: React.FC = () => {
  const { radiusForm, setRadiusForm, errMsg, setErrMsg, submitHandler, cancelHandler, serverAddress, sharedSecret, confirmSharedSecret, port, profileAttributeType } = useRadiusData();
  const dispatch = useStoreDispatch();
  const storeErrorMsg = useSelector(errorMsgSelector);

  /* Adapter: Radix InputField/PasswordField onChange(name, value) → hook state */
  const handleChange = (name: string, value: string) => {
    setRadiusForm({ ...radiusForm, [name]: value });
    setErrMsg({ ...errMsg, [name]: "" });
    dispatch(setErrorMsg({ ...storeErrorMsg, [name]: "" }));
  };

  const handleOnCheck = (checked: boolean) => {
    if (checked) {
      setRadiusForm({ ...radiusForm, isChangeSharedSecret: checked });
      setErrMsg({ ...errMsg, sharedSecret: "", confirmSharedSecret: "" });
    } else {
      setRadiusForm({ ...radiusForm, sharedSecret: "", confirmSharedSecret: "", isChangeSharedSecret: checked });
      setErrMsg({ ...errMsg, sharedSecret: "", confirmSharedSecret: "" });
    }
  };

  const blurHandler = () => {
    const errorMessages = validateRadius(radiusForm);
    setErrMsg({ ...errMsg, ...errorMessages });
    dispatch(setErrorMsg({ ...storeErrorMsg, ...errorMessages }));
  };

  const isValidFormData = radiusForm?.isChangeSharedSecret
    ? !!radiusForm.serverAddress &&
    !!radiusForm.sharedSecret &&
    !!radiusForm.confirmSharedSecret &&
    !!radiusForm.port &&
    !!radiusForm.profileAttributeType
    : !!radiusForm.serverAddress && !!radiusForm.port && !!radiusForm.profileAttributeType;
  const isDisableSave = isValidFormData && !isError(errMsg);

  return (

    <div
      className="flex flex-col w-full overflow-y-auto px-1 md:px-1"
      style={{ color: "var(--foreground)" }}
    >
      {/* Heading */}
       <h2 style={{ margin: 0, padding: 0, lineHeight: 0.5, marginBottom: "10px" }}>
        <ShadcnHeading className="mb-0 text-sm font-semibold text-foreground" as="span">
          RADIUS Settings
        </ShadcnHeading>
      </h2>
      <div className="mt-3"
        style={{
          border: "1px solid var(--color-border)",
          borderRadius: "var(--radius)",
          padding: "24px",
          boxShadow: "var(--shadow-card)",
          backgroundColor: "var(--background)",
        }}
      >
        <form onSubmit={submitHandler} autoComplete="off">
          <div className="space-y-5 max-w-lg">
            {/* Server Address */}
            <InputField
              required
              label="Server Address"
              name={serverAddress}
              value={radiusForm?.serverAddress}
              maxLength={255}
              placeholder="Enter server address"
              onChange={handleChange}
              onBlur={blurHandler}
              tooltipText="Maximum length: 255 characters"
            />

            {/* Change Shared Secret checkbox */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="isChangeSharedSecret"
                checked={radiusForm?.isChangeSharedSecret}
                onCheckedChange={(checked) => handleOnCheck(!!checked)}
                className="m-0"
              />
              <Label htmlFor={"isChangeSharedSecret"} className="text-sm font-medium">
                Change Shared Secret
              </Label>
            </div>

            {/* Shared Secret */}
            <PasswordField
              label="Shared Secret"
              name={sharedSecret}
              value={radiusForm?.sharedSecret ?? ""}
              disabled={!radiusForm.isChangeSharedSecret}
              onChange={handleChange}
              placeholder="Enter shared secret"
              onBlur={blurHandler}
            />

            {/* Confirm Shared Secret */}
            <PasswordField
              label="Confirm Shared Secret"
              name={confirmSharedSecret}
              value={radiusForm?.confirmSharedSecret ?? ""}
              disabled={!radiusForm.isChangeSharedSecret}
              onChange={handleChange}
              placeholder="Confirm shared secret"
              onBlur={blurHandler}
            />

            {/* Port */}
            <InputField
              required
              label="Port"
              name={port}
              type="number"
              value={radiusForm?.port}
              min={1}
              max={65535}
              placeholder="Enter port number"
              onChange={handleChange}
              onBlur={blurHandler}
            />

            {/* Profile Attribute Type */}
            <InputField
              required
              label="Profile Attribute Type"
              name={profileAttributeType}
              type="number"
              value={radiusForm?.profileAttributeType}
              min={1}
              max={255}
              placeholder="Enter attribute type"
              onChange={handleChange}
              onBlur={blurHandler}
            />
            {!errMsg?.profileAttributeType && !storeErrorMsg?.profileAttributeType && (
              <p className="text-xs text-muted-foreground mt-1">
                Represent RADIUS attribute type (value between 1 to 255), this attribute will hold the 'profile name' in RADIUS server user
              </p>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" type="button" onClick={cancelHandler}>
                Cancel
              </Button>
              <Button type="submit" disabled={!isDisableSave}>
                Save
              </Button>
            </div>

          </div>
        </form>
      </div>
    </div>

  );
};

export default Radius;
